"use client";

import { useEffect, useState, useTransition } from "react";
import type { Dict } from "@/lib/i18n";
import { sendCode, verifyCode } from "./actions";
import type { SendResult, VerifyError } from "./actions";

const input =
  "h-14 w-full rounded-2xl border-2 border-transparent bg-paper px-4 text-lg outline-none focus:border-stamp";
const primary = "h-14 w-full rounded-2xl bg-stamp text-[17px] font-medium text-white disabled:opacity-60";

type Labels = Omit<Dict["login"], "sentTo"> & { sentBefore: string; sentAfter: string };

export function LoginForm({ t, next }: { t: Labels; next: string }) {
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [typed, setTyped] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<React.ReactNode>(null);
  const [devCode, setDevCode] = useState<string | undefined>();
  const [wait, setWait] = useState(0);
  const [resent, setResent] = useState(false);
  const [pending, start] = useTransition();

  // one timeout per second, re-armed whenever wait changes, so a restart from a
  // fresh resendIn can't leave a stale timer behind
  useEffect(() => {
    if (step !== "code" || wait <= 0) return;
    const id = setTimeout(() => setWait((w) => Math.max(0, w - 1)), 1000);
    return () => clearTimeout(id);
  }, [step, wait]);

  const messages: Record<Exclude<SendResult, { ok: true }>["error"] | VerifyError, string> = {
    invalid_phone: t.invalidPhone,
    bad_code: t.badCode,
    expired: t.expired,
    too_many_attempts: t.tooManyAttempts,
    rate_limited: t.rateLimited,
    send_failed: t.sendFailed,
    cooldown: "",
  };

  const countdown = (n: number) => (
    <>
      {t.resendWait} <span className="num">{n}</span>
      {t.seconds}
    </>
  );

  function send(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResent(false);
    start(async () => {
      const res = await sendCode(typed);
      if (res.ok) {
        setPhone(res.phone);
        setDevCode(res.devCode);
        setWait(res.resendIn);
        setCode("");
        setStep("code");
      } else if (res.error === "cooldown") {
        // the earlier code for this number is still good, so carry on with it
        if (res.phone === phone) {
          setWait(res.retryIn);
          setStep("code");
        } else setError(countdown(res.retryIn));
      } else setError(messages[res.error]);
    });
  }

  function resend() {
    setError(null);
    setResent(false);
    start(async () => {
      const res = await sendCode(phone);
      if (res.ok) {
        setCode("");
        setDevCode(res.devCode);
        setWait(res.resendIn);
        setResent(true);
      } else if (res.error === "cooldown") setWait(res.retryIn);
      else setError(messages[res.error]);
    });
  }

  function verify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResent(false);
    start(async () => {
      // on success the action redirects, so a return means something failed
      const res = await verifyCode(phone, code, next);
      if (res) setError(messages[res.error]);
    });
  }

  return step === "phone" ? (
    <form onSubmit={send} className="mt-8 grid gap-3">
      <label htmlFor="phone" className="text-sm font-medium">
        {t.phoneLabel}
      </label>
      <input
        id="phone"
        type="tel"
        dir="ltr"
        inputMode="tel"
        autoComplete="tel"
        placeholder="05x xxx xxxx"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        className={`${input} num text-start`}
        aria-describedby="phone-hint"
        aria-invalid={!!error}
        required
      />
      <p id="phone-hint" className="text-sm text-ink-soft">
        {t.phoneHint}
      </p>
      {error && (
        <p role="alert" className="text-sm text-alert">
          {error}
        </p>
      )}
      <button type="submit" disabled={pending || !typed.trim()} className={`${primary} mt-2`}>
        {pending ? t.sending : t.send}
      </button>
    </form>
  ) : (
    <form onSubmit={verify} className="mt-8 grid gap-3">
      <p className="text-[15px]">
        {t.sentBefore}
        <span className="num font-medium">{phone}</span>
        {t.sentAfter}
      </p>
      <label htmlFor="code" className="mt-2 text-sm font-medium">
        {t.codeLabel}
      </label>
      <input
        id="code"
        type="text"
        dir="ltr"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        pattern="[0-9]*"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className={`${input} num text-center text-2xl tracking-[0.4em]`}
        aria-describedby={devCode ? "code-hint" : undefined}
        aria-invalid={!!error}
        autoFocus
        required
      />
      {devCode && (
        <p id="code-hint" className="text-sm text-ink-soft">
          {t.devHint} <span className="num font-medium">{devCode}</span>
        </p>
      )}
      {resent && (
        <p role="status" className="text-sm text-ink-soft">
          {t.resent}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-alert">
          {error}
        </p>
      )}
      <button type="submit" disabled={pending || code.trim().length < 6} className={`${primary} mt-2`}>
        {pending ? t.verifying : t.verify}
      </button>
      {/* the countdown is hidden from readers so it isn't announced every second; the button turning up is */}
      <div aria-live="polite" className="grid">
        {wait > 0 ? (
          <p aria-hidden="true" className="text-center text-[15px] leading-12 text-ink-soft">
            {countdown(wait)}
          </p>
        ) : (
          <button
            type="button"
            onClick={resend}
            disabled={pending}
            className="min-h-12 text-[15px] text-stamp underline underline-offset-4 disabled:opacity-60"
          >
            {t.resend}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          setStep("phone");
          setCode("");
          setError(null);
          setResent(false);
        }}
        className="min-h-12 text-[15px] text-stamp underline underline-offset-4"
      >
        {t.change}
      </button>
    </form>
  );
}
